// apps/client/src/components/brokers/ChangeBrokerNameModal.tsx
// Enterprise-grade broker name change modal
// Pattern: Follow TransferBrokerModal.tsx structure

import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { Timestamp } from 'firebase/firestore';

interface ChangeBrokerNameModalProps {
  show: boolean;
  onClose: () => void;
  broker: {
    id: string;
    currentName: string;
    legalName: string;
    nameHistory?: Array<{
      name: string;
      effectiveFrom: Timestamp;
      effectiveTo: Timestamp | null;
      reason?: string;
    }>;
  } | null;
  companyId: string;
  onSuccess?: () => void;
}

type NameChangeReason = 'marriage' | 'legal_name_change' | 'correction' | 'other';

const ChangeBrokerNameModal: React.FC<ChangeBrokerNameModalProps> = ({
  show,
  onClose,
  broker,
  companyId,
  onSuccess
}) => {
  const [newName, setNewName] = useState('');
  const [reason, setReason] = useState<NameChangeReason | ''>('');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setNewName('');
      setReason('');
      setEffectiveDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setError('');
    }
  }, [show]);

  const handleChangeName = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!broker || !newName.trim() || !reason) {
      setError('Please fill in all required fields');
      return;
    }

    const trimmedNewName = newName.trim();

    if (trimmedNewName.length < 2) {
      setError('New name must be at least 2 characters');
      return;
    }

    if (trimmedNewName === broker.currentName) {
      setError('New name must be different from current name');
      return;
    }

    if (reason === 'other' && !notes.trim()) {
      setError('Please provide notes when selecting "Other" as the reason');
      return;
    }

    setChanging(true);

    try {
      const changeBrokerNameFn = httpsCallable(functions, 'changeBrokerName');

      const result = await changeBrokerNameFn({
        brokerId: broker.id,
        companyId,
        newName: trimmedNewName,
        reason,
        effectiveDate: effectiveDate || undefined,
        notes: notes.trim() || undefined
      });

      const response = result.data as { success: boolean; message: string };

      if (response.success) {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        setError(response.message || 'Name change failed');
      }
    } catch (err: any) {
      console.error('Name change error:', err);
      const errorMessage = err.message || 'Failed to change broker name. Please try again.';
      setError(errorMessage);
    } finally {
      setChanging(false);
    }
  };

  const reasonOptions: Array<{ value: NameChangeReason; label: string; description: string }> = [
    {
      value: 'marriage',
      label: 'Marriage/Partnership',
      description: 'Name changed due to marriage or partnership'
    },
    {
      value: 'legal_name_change',
      label: 'Legal Name Change',
      description: 'Official legal name change (deed poll, court order, etc.)'
    },
    {
      value: 'correction',
      label: 'Correction',
      description: 'Correcting a previous error or misspelling'
    },
    {
      value: 'other',
      label: 'Other',
      description: 'Other reason (please specify in notes)'
    }
  ];

  const formatTimestamp = (timestamp: Timestamp | null): string => {
    if (!timestamp) return 'Present';
    try {
      return timestamp.toDate().toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (!broker) return null;

  const showPreview = newName.trim() && newName.trim() !== broker.currentName;

  return (
    <Modal
      title="Change Broker Name"
      show={show}
      onClose={onClose}
    >
      <form onSubmit={handleChangeName}>
        {/* Name Change Preview */}
        <div className="bg-slate-700 rounded-lg p-4 mb-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Current Name:</span>
              <span className="text-white font-medium">{broker.currentName}</span>
            </div>

            {showPreview && (
              <>
                <div className="flex items-center justify-center text-slate-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">New Name:</span>
                  <span className="text-white font-medium">{newName.trim()}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Name History (if exists) */}
        {broker.nameHistory && broker.nameHistory.length > 0 && (
          <div className="mb-4 bg-slate-700 rounded-lg p-3">
            <h4 className="text-sm font-medium text-slate-300 mb-2">Previous Name History</h4>
            <div className="space-y-1 text-xs text-slate-400">
              {broker.nameHistory.slice().reverse().map((entry, idx) => (
                <div key={idx} className="flex items-start justify-between py-1 border-b border-slate-600 last:border-b-0">
                  <span className="font-medium text-slate-300">{entry.name}</span>
                  <span className="text-slate-500 text-right ml-2">
                    {formatTimestamp(entry.effectiveFrom)} - {formatTimestamp(entry.effectiveTo)}
                    {entry.reason && <span className="block text-slate-600">({entry.reason})</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            New Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            placeholder="Enter new broker name..."
            required
            minLength={2}
            autoFocus
          />
          <p className="mt-1 text-xs text-slate-400">
            Enter the broker's new legal name (minimum 2 characters)
          </p>
        </div>

        {/* Reason (Required) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Reason for Name Change <span className="text-red-400">*</span>
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as NameChangeReason)}
            className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            required
          >
            <option value="">Select a reason...</option>
            {reasonOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {reason && (
            <p className="mt-1 text-xs text-slate-400">
              {reasonOptions.find(o => o.value === reason)?.description}
            </p>
          )}
        </div>

        {/* Effective Date */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Effective Date
          </label>
          <input
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            max={new Date().toISOString().split('T')[0]} // Can't be future date
          />
          <p className="mt-1 text-xs text-slate-400">
            When did this name change take effect? (defaults to today)
          </p>
        </div>

        {/* Notes (Optional, Required if "other") */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Notes {reason === 'other' && <span className="text-red-400">*</span>}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            rows={3}
            placeholder={reason === 'other' ? 'Please specify the reason for this name change...' : 'Optional additional context for this name change...'}
            required={reason === 'other'}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-red-900 border border-red-700 text-red-200 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Important Notice */}
        <div className="mb-6 bg-blue-900 border border-blue-700 text-blue-200 p-3 rounded-md text-sm">
          <p className="font-medium mb-1">Important:</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>This will update the broker's name across all records</li>
            <li>Historical commission data will remain attributed to this broker</li>
            <li>The change will be recorded in the broker's name history</li>
            <li>This action is auditable and cannot be undone (but new changes can be made)</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={changing}
            className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-500 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={changing || !newName.trim() || !reason || newName.trim() === broker.currentName}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {changing ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Changing Name...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Confirm Name Change
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ChangeBrokerNameModal;
